import { compile } from "path-to-regexp";

import { mergeParsers, parse, type Parser } from "../parsing/index.js";
import type RouteNavigator from "./Navigator.js";
import { doesPathMatchPattern } from "./pathParser.js";
import type {
    AnyPathParams,
    AnyQueryParams,
    Inherit,
    NoPathParams,
    NoQueryParams,
    PathParamsBase,
    QueryParamsBase,
} from "./types.js";
import { joinPaths } from "./utilities.js";

export type GetLocationArgs<
    PathParams extends PathParamsBase,
    QueryParams extends QueryParamsBase,
> = NoPathParams extends PathParams
    ? NoQueryParams extends QueryParams
        ? []
        : [params: { queryParams: QueryParams }]
    : NoQueryParams extends QueryParams
      ? [params: { pathParams: PathParams }]
      : [params: { pathParams: PathParams; queryParams: QueryParams }];

export default class Route<
    PathParams extends PathParamsBase | NoPathParams = NoPathParams,
    QueryParams extends QueryParamsBase | NoQueryParams = NoQueryParams,
> {
    path: string | null = null;
    pathParamsSchema: Parser<PathParams> | null = null;
    queryParamsSchema: Parser<QueryParams> | null = null;
    parent: RouteNavigator<AnyPathParams, AnyQueryParams> | null = null;

    // private get path(): string {
    //     return joinPaths(this.parent ? this.parent.path : null, this._path);
    // }

    // Definitions methods

    setPath(path: string): Route<NoPathParams, QueryParams>;
    setPath<S extends PathParamsBase>(path: string, schema: Parser<S>): Route<S, QueryParams>;
    setPath<S extends PathParamsBase>(path: string, schema?: Parser<S>) {
        if (!schema) {
            const route = this.clone<NoPathParams, QueryParams>();
            route.path = path;
            route.pathParamsSchema = null;
            return route;
        } else {
            const route = this.clone<S, QueryParams>();
            route.path = path;
            route.pathParamsSchema = schema;
            return route;
        }
    }

    setQueryParamsSchema<S extends QueryParamsBase>(schema: Parser<S>) {
        const route = this.clone<PathParams, S>();
        route.queryParamsSchema = schema;
        return route;
    }

    setNavigator<
        ParentPathParams extends PathParamsBase | never,
        ParentQueryParams extends QueryParamsBase | never,
    >(
        navigator: RouteNavigator<ParentPathParams, ParentQueryParams>,
    ): Route<Inherit<ParentPathParams, PathParams>, Inherit<ParentQueryParams, QueryParams>> {
        const route = this.clone<
            Inherit<ParentPathParams, PathParams>,
            Inherit<ParentQueryParams, QueryParams>
        >();
        route.path = joinPaths(navigator.path, this.path);
        route.pathParamsSchema = mergeParsers(
            navigator.pathParamsSchema,
            this.pathParamsSchema,
        ) as Parser<Inherit<ParentPathParams, PathParams>>;
        route.queryParamsSchema = mergeParsers(
            navigator.queryParamsSchema,
            this.queryParamsSchema,
        ) as Parser<Inherit<ParentQueryParams, QueryParams>>;
        route.parent = navigator;
        return route;
    }

    private clone<
        NewPathParams extends PathParamsBase | never,
        NewQueryParams extends QueryParamsBase | never,
    >(): Route<NewPathParams, NewQueryParams> {
        const route = new Route<NewPathParams, NewQueryParams>();
        route.path = this.path;
        route.pathParamsSchema = this.pathParamsSchema as Route<
            NewPathParams,
            NewQueryParams
        >["pathParamsSchema"];
        route.queryParamsSchema = this.queryParamsSchema as Route<
            NewPathParams,
            NewQueryParams
        >["queryParamsSchema"];
        route.parent = this.parent;
        return route;
    }

    // Reading

    private getPathPattern() {
        if (!this.path) {
            return "/";
        } else {
            return this.path;
        }
    }

    matches(pathname: string): boolean {
        const matching = doesPathMatchPattern(pathname, this.getPathPattern());
        return matching !== false;
    }

    getLocation(...args: GetLocationArgs<PathParams, QueryParams>): string {
        const arg = args[0];

        let output: string = "";

        // Path
        if (!this.path) {
            output += "/";
        } else {
            if (this.pathParamsSchema) {
                const pathParams = arg && "pathParams" in arg ? arg.pathParams : {};
                const parsed = parse(pathParams, this.pathParamsSchema);
                const compiler = compile(this.path);
                output += compiler(parsed);
            } else {
                output += this.path;
            }
        }

        return output;

        // const queryParams = arg && "queryParams" in arg ? arg.queryParams : {};

        // const pathname = this.path.replace(/:([A-Za-z_][A-Za-z0-9_]*)/g, (_, name: string) => {
        //     const value = name in pathParams ? pathParams[name] : undefined;
        //     if (value === undefined) throw new Error(`Missing path param "${name}"`);
        //     return encodeURIComponent(value);
        // });

        // if (!params?.queryParams) return pathname;

        // const search = Object.entries(params.queryParams)
        //     .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        //     .join("&");
        // return search === "" ? pathname : `${pathname}?${search}`;
    }

    getPathParams(pathname: string): PathParams {
        const matching = doesPathMatchPattern(pathname, this.getPathPattern());
        if (!this.pathParamsSchema) throw new Error(`Route ${this.path} has no path params schema`);
        if (!matching) throw new Error(`Route ${this.path} does not match path ${pathname}`);
        const parsed = parse(matching, this.pathParamsSchema);
        return parsed;
    }

    // Attaching

    // matches(pathname: string): boolean {
    //     return matchPath(this.path, pathname, "exact");
    // }

    // getParams(pathname: string): PathParams {
    //     if (!this.matches(pathname)) {
    //         throw new Error(`Route ${this.path} does not match path ${pathname}`);
    //     }

    //     const patternSegments = this.path.split("/").filter((segment) => segment !== "");
    //     const pathSegments = pathname.split("/").filter((segment) => segment !== "");
    //     const pathParams: PathParamsBase = {};

    //     for (const [index, patternSegment] of patternSegments.entries()) {
    //         if (!patternSegment.startsWith(":")) continue;
    //         const pathSegment = pathSegments[index];
    //         if (pathSegment === undefined) continue;
    //         pathParams[patternSegment.slice(1)] = decodeURIComponent(pathSegment);
    //     }

    //     const parsers = this.getPathParamsParsers();
    //     if (parsers.length === 0) return pathParams as PathParams;

    //     const [first, ...rest] = parsers;
    //     if (!first) return pathParams as PathParams;
    //     if (rest.length === 0) return parse(pathParams, first as Parser<PathParams>);
    //     return Object.assign(
    //         {},
    //         parse(pathParams, first),
    //         ...rest.map((parser) => parse(pathParams, parser)),
    //     ) as PathParams;
    // }

    // getQueryParamsParsers(): Parser<unknown>[] {
    //     const parsers = this.parent?.getQueryParamsParsers() ?? [];
    //     if (this.queryParamsSchema) parsers.push(this.queryParamsSchema as Parser<unknown>);
    //     return parsers;
    // }

    // getPathParamsParsers(): Parser<unknown>[] {
    //     const parsers = this.parent?.getPathParamsParsers() ?? [];
    //     if (this.pathParamsSchema) parsers.push(this.pathParamsSchema as Parser<unknown>);
    //     return parsers;
    // }
}
